import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Map,
  Dashboard,
  People,
  Description,
  Person,
  Lock,
} from "@mui/icons-material";
import { useAuth } from "./context/AuthContext";
import CheckingAuthDialog from "./components/CheckingAuthDialog";
import AppHeader from "./components/TopBar";
import { canAccessFeature, FEATURES } from "./utils/authPermissions";
import { APP_COLORS as C } from "./theme/colors";
// import hcgLogo from "./assets/Mapgeoid-logo.jpeg";
// import mapgeoidLogo from "./assets/logo.png";

const NeonNetworkBackground = () => {
  useEffect(() => {
    const canvas = document.getElementById("neon-bg");
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const nodes = [];
    const total = 40;

    for (let i = 0; i < total; i++) {
      nodes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
      });
    }

    let animationId;

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.strokeStyle = "rgba(100, 200, 255, 0.15)";
      ctx.lineWidth = 1;

      for (let i = 0; i < nodes.length; i++) {
        const n1 = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const n2 = nodes[j];
          const dist = Math.sqrt((n1.x - n2.x) ** 2 + (n1.y - n2.y) ** 2);
          if (dist < 200) {
            ctx.globalAlpha = 1 - dist / 200;
            ctx.strokeStyle = "rgba(100, 200, 255, 0.6)";
            ctx.beginPath();
            ctx.moveTo(n1.x, n1.y);
            ctx.lineTo(n2.x, n2.y);
            ctx.stroke();
          }
        }
      }

      ctx.globalAlpha = 1;

      nodes.forEach((n) => {
        ctx.beginPath();
        ctx.fillStyle = "#60c5ff";
        ctx.shadowColor = "#60c5ff";
        ctx.shadowBlur = 15;
        ctx.arc(n.x, n.y, 3, 0, Math.PI * 2);
        ctx.fill();

        n.x += n.vx;
        n.y += n.vy;

        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
      });

      animationId = requestAnimationFrame(draw);
    }

    draw();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <canvas
      id="neon-bg"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
      }}
    />
  );
};

const HaryanaGasPortal = ({
  onNavigateToMap,
  onNavigateToDashboard,
  onNavigateToUsers,
}) => {
  const {
    user,
    isAuthenticated,
    isChecking,
    login: authLogin,
  } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false); // ← ADD
  const [error, setError] = useState(""); // ← ADD
  const [success, setSuccess] = useState("");
  const redirectTimerRef = useRef(null);

  const showLogin = !isChecking && !isAuthenticated;

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const userData = await authLogin(username, password);
      setSuccess("Login successful!");
      redirectTimerRef.current = setTimeout(() => {
        setSuccess("");
      }, 1500);
      setUsername("");
      setPassword("");
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureClick = (onClick) => {
    if (!isAuthenticated) {
      setError("Please login first to access this feature");
      return;
    }
    onClick();
  };

  const allFeatures = [
    {
      icon: <Map sx={{ fontSize: 40 }} />,
      title: "Map View",
      description: "Interactive map to view & analyze gas pipeline network",
      onClick: onNavigateToMap,
      feature: FEATURES.mapview,
    },
    {
      icon: <Dashboard sx={{ fontSize: 40 }} />,
      title: "Dashboard",
      description: "View live analytics, charts & KPIs of gas distribution",
      onClick: onNavigateToDashboard,
      feature: FEATURES.dashboard,
    },
    {
      icon: <People sx={{ fontSize: 40 }} />,
      title: "User Management",
      description:
        "Manage portal users, assign roles & control access to GIS features and data layers.",
      onClick: onNavigateToUsers,
      feature: FEATURES.users,
    },
    {
      icon: <Description sx={{ fontSize: 40 }} />,
      title: "Construction Management",
      description:
        "Track pipeline laying progress, monitor contractors & manage new connection work orders",
      onClick: () => alert("Construction Management coming soon"),
      feature: FEATURES.construction,
    },
  ];

  const features = allFeatures.filter((f) =>
    user?.role ? canAccessFeature(user.role, f.feature) : false,
  );

  const FeatureCard = ({ icon, title, description, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Card
        onClick={() => handleFeatureClick(onClick)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          background: isHovered
            ? "rgba(255, 255, 255, 1)"
            : "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: "1rem",
          padding: "1.5rem",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          textAlign: "center",
          transition: "all 0.3s ease",
          cursor: "pointer",
          transform: isHovered ? "scale(1.05)" : "scale(1)",
          boxShadow: isHovered
            ? "0 20px 60px rgba(0, 0, 0, 0.3)"
            : "0 4px 12px rgba(0, 0, 0, 0.1)",
          width: "100%",
          maxWidth: 280,
        }}
        elevation={0}
      >
        <CardContent sx={{ padding: 0 }}>
          <Box
            sx={{
              width: "70px",
              height: "70px",
              margin: "0 auto 1rem",
              background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              transition: "all 0.3s",
              transform: isHovered ? "scale(1.1) rotate(5deg)" : "scale(1)",
            }}
          >
            {icon}
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1.15rem",
              fontWeight: "bold",
              color: "#1e293b",
              marginBottom: "0.5rem",
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              color: "#64748b",
              lineHeight: "1.6",
            }}
          >
            {description}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  return (
    <div style={{ height: "100vh", overflow: "hidden" }}>
      <CheckingAuthDialog open={isChecking} />
      <style>
        {`
          @keyframes float {
            0%, 100% { 
              transform: translateY(0px); 
              opacity: 0.4; 
            }
            50% { 
              transform: translateY(-15px); 
              opacity: 0.7; 
            }
          }
          @keyframes pulse {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 0.6; }
          }
        `}
      </style>

      <Box
        sx={{
          position: "relative",
          height: "100vh",
          width: "100%",
          overflow: "hidden",
          background:
            "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #334155 100%)",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {/* Animated Background */}
        <NeonNetworkBackground />

        {/* Main content: header + hero + footer */}
        <Box
          sx={{
            position: "relative",
            zIndex: 10,
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
          }}
        >
          {/* Header */}
          <Box sx={{ flexShrink: 0 }}>
            <AppHeader showBreadcrumbs={false} sx={{ bgcolor: "transparent", borderBottom: "none" }} />
          </Box>

          {/* Hero — title + cards centered vertically between header and footer */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
              py: { xs: 3, md: 4 },
            }}
          >
            <Box sx={{ textAlign: "center", mb: { xs: 2, md: 3 }, maxWidth: 900 }}>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "2rem", sm: "2.75rem", md: "3.75rem" },
                  fontWeight: "bold",
                  letterSpacing: "-0.02em",
                  textShadow: "0 4px 20px rgba(59, 130, 246, 0.5)",
                  background: "linear-gradient(135deg, #ffffff 0%, #60c5ff 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                BlueView for City Gas Distribution
              </Typography>
            </Box>

            {/* Feature cards — always centered as a group */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 280px))",
                justifyContent: "center",
                gap: "1.5rem",
                width: "100%",
                maxWidth: "1200px",
                mx: "auto",
              }}
            >
              {features.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Developer credits — fixed bottom-left */}
        <Box
          component="footer"
          sx={{
            position: "fixed",
            bottom: { xs: 12, md: 20 },
            left: { xs: 12, md: 24 },
            zIndex: 100,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.25,
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: "rgba(15, 23, 42, 0.8)",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.3)",
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/assets/logo.png`}
              alt="MapGeoid"
              sx={{
                width: 28,
                height: 28,
                objectFit: "contain",
                display: "block",
              }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.8rem",
                color: "#ffffff",
                letterSpacing: "0.06em",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              BLUEVIEW BY MAPGEOID
            </Typography>
          </Box>
        </Box>

        {/* Login Modal — non-closable until authenticated */}
        {showLogin && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0, 0, 0, 0.5)",
            }}
          >
            <Card
              sx={{
                maxWidth: "400px",
                maxHeight :"800px",
                width: "100%",
                mx: 2,
                padding: "3rem 2.5rem",
                background: C.surface,
                borderRadius: "12px",
                border: `1px solid ${C.borderBlue}`,
                boxShadow: "0 8px 32px rgba(0, 51, 118, 0.15)",
                position: "relative",
              }}
            >
              {/* HCG Logo */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "1rem",
                }}
              >
                {/* <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/assets/Mapgeoid-logo.jpeg`}
                  alt="HCG Logo"
                  sx={{
                    width: 300,
                    height: 50,
                    objectFit: "contain",
                  }}
                /> */}
              </Box>

              <Typography
                variant="h4"
                sx={{
                  textAlign: "center",
                  fontWeight: "bold",
                  color: C.primary,
                  marginBottom: "2.5rem",
                  fontSize: "2rem",
                }}
              >
                Sign In
              </Typography>
              {/* ADD THESE ALERTS */}
              {error && (
                <Alert severity="error" sx={{ marginBottom: "1rem" }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ marginBottom: "1rem" }}>
                  {success}
                </Alert>
              )}

              {/* Login Form */}
              <Box
                component="form"
                onSubmit={handleLogin}
                sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
              >
                {/* Username Field */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Person sx={{ fontSize: "1.2rem", color: C.primary }} />
                    <Typography
                      sx={{
                        fontWeight: "600",
                        color: C.text,
                        fontSize: "0.95rem",
                      }}
                    >
                      Username
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    variant="outlined"
                    required
                    disabled={loading} // ← ADD THIS LINE
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        background: "#f8fafc",
                        "& fieldset": {
                          borderColor: "#e2e8f0",
                        },
                        "&:hover fieldset": {
                          borderColor: "#cbd5e1",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: C.primary,
                        },
                      },
                      "& input": {
                        padding: "0.9rem 1rem",
                      },
                    }}
                  />
                </Box>

                {/* Password Field */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Lock sx={{ fontSize: "1.2rem", color: C.primary }} />
                    <Typography
                      sx={{
                        fontWeight: "600",
                        color: C.text,
                        fontSize: "0.95rem",
                      }}
                    >
                      Password
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    variant="outlined"
                    required
                    disabled={loading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        background: "#f8fafc",
                        "& fieldset": {
                          borderColor: "#e2e8f0",
                        },
                        "&:hover fieldset": {
                          borderColor: "#cbd5e1",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: C.primary,
                        },
                      },
                      "& input": {
                        padding: "0.9rem 1rem",
                      },
                    }}
                  />
                </Box>

                {/* Login Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    padding: "0.9rem",
                    borderRadius: "8px",
                    bgcolor: C.primary,
                    textTransform: "none",
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    letterSpacing: "0.5px",
                    boxShadow: "0 4px 12px rgba(0, 51, 118, 0.3)",
                    "&:hover": {
                      bgcolor: C.primaryDark,
                      boxShadow: "0 6px 16px rgba(0, 51, 118, 0.35)",
                    },
                    "&:disabled": {
                      bgcolor: "#cbd5e1",
                    },
                  }}
                >
                  {loading ? ( // ← REPLACE "LOGIN" WITH THIS
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  ) : (
                    "LOGIN"
                  )}
                </Button>
              </Box>
            </Card>
          </Box>
        )}
      </Box>
    </div>
  );
};
export default React.memo(HaryanaGasPortal);
// export default HaryanaGasPortal;
